import { Chip, Divider, Grid, Paper, Stack, Typography } from '@mui/material'
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { GetMarketNewsResponse, NewsArticle } from '../../proto/generated/market_news'
import InfiniteScroll from 'react-infinite-scroll-component'
import { getMarketNews } from '../../services/apis/misc'
import { useDispatch,useSelector } from 'react-redux'
import ListSkeleton from '../common/ListSkeleton'
import { selectCommonStore } from '../../selectors/common'
import { WidgetListItems } from '../../util/sidebar'
import { selectPrimarySecurity } from '../../selectors/home'
import { EmptyState, GhostRows } from '../common/EmptyState'
import { Exchange } from '../../proto/generated/common'
import { isFnOSecurity } from '../../util/security'
import { Security } from '../../proto/generated/security'
import NewsIcon from '../../assets/icons/NewsIcon'
import { makeStyles } from 'tss-react/mui'
import NewsWidget from './NewsWidget'
import commonActionTypes from "../../actionTypes/common.actionTypes";

type NewsTagType = {
    key: string | undefined,
    label: string | undefined
}

const useStyles = makeStyles()((theme) => ({
    paper: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        borderTopRightRadius: '8px'
    },
    newsHeader: {
        padding: theme.spacing(0, 1)
    },
    newsHeaderIcon: {
        color: theme.palette.text.secondary
    },
    tagStack: {
        margin: theme.spacing(1, 2),
        flexDirection: 'row',
        gap: theme.spacing(1)
    },
    scrollableGrid: {
        flex: 1,
        display: 'flex',
        overflowY: 'auto'
    },
    emptyState: {
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center'
    },
    endMessage: {
        padding: theme.spacing(4),
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'row'
    }
}))

function MarketNews() {
    const [loading, setLoading] = useState<boolean>(true)
    const [news, setNews] = useState<NewsArticle[]>([])
    const [totalArticles, setTotalArticles] = useState<number>(0)
    const pageNo = useRef<number>(0)
    // Monotonic id of the latest list request. Security and tag changes can fire overlapping
    // requests, and a stale one must neither overwrite the list nor clear the loading state.
    const requestSeq = useRef(0)
    const { rightSideWidget,isNewsCategoryAll } = useSelector(selectCommonStore)
    const pageSize = 10
    const { primary, underlying, expressionMode, expressionSecurities } = useSelector(selectPrimarySecurity)
    const [selectedSecurity, setSelectedSecurity] = useState<Security>()
    const [tags, setTags] = useState<NewsTagType[]>([])
    const [newsSelectedTag, setNewsSelectedTag] = useState<string | undefined>()
    const { classes } = useStyles()
    const dispatch = useDispatch()

    useEffect(() => {
        let security = expressionMode ? expressionSecurities.find((security) => !isFnOSecurity(security)) : (primary && isFnOSecurity(primary) ? underlying : primary)
        if (!security) {
            security = {
                code: 'NIFTY_50',
                displayName: 'NIFTY 50',
                exchange: Exchange.NSE
            }
        }
        if (selectedSecurity?.code !== security.code) {
            setSelectedSecurity(security)
            setTags([{
                key: security?.companyCode || security?.code,
                label: security?.displayName
            }, {
                key: 'All',
                label: 'All'
            }])
        }
    }, [primary, underlying, expressionMode, expressionSecurities])

    useEffect(() => {
        setNewsSelectedTag(selectedSecurity?.companyCode || selectedSecurity?.code)
    }, [selectedSecurity])

    useEffect(() => {
        if (rightSideWidget === WidgetListItems.MARKET_NEWS && selectedSecurity) {
            const seq = ++requestSeq.current
            setLoading(true)
            fetchMarketNews(0, pageSize)
                .then((response) => {
                    if (seq !== requestSeq.current) return
                    setTotalArticles(response.totalCount)
                    setNews(response.newsArticles)
                    pageNo.current = 0
                })
                .finally(() => {
                    if (seq === requestSeq.current) setLoading(false)
                })
        }
    }, [isNewsCategoryAll, newsSelectedTag, rightSideWidget, selectedSecurity])

    const fetchData = () => {
        fetchMarketNews(pageNo.current + 1, pageSize).then((response) => {
            setTotalArticles(response.totalCount)
            setNews([...news, ...response.newsArticles])
            pageNo.current = pageNo.current + 1
        })
    }

    const fetchMarketNews = async (pageNo: number, pageSize: number): Promise<GetMarketNewsResponse> => {
        try {
            return await getMarketNews({
                pageNumber: pageNo,
                pageSize: pageSize,
                companyCode: isNewsCategoryAll ? '' : selectedSecurity?.companyCode || selectedSecurity?.code || ''
            })
        } catch (err) {
            return Promise.reject(err)
        }
    }

    const handleTagClick = useCallback((key: React.SetStateAction<string | undefined>) => {
            setNewsSelectedTag(key)
            dispatch({
                type: commonActionTypes.SET_NEWS_CATEGORY,
                payload: key === 'All'
            })
        },
        [setNewsSelectedTag, dispatch])

    return (
        <Paper className={classes.paper}>
            <Stack height={'100%'}>
                <Stack>
                    <Stack direction={'row'} alignItems={'center'} className={classes.newsHeader}>
                        <NewsIcon className={classes.newsHeaderIcon} />
                        <Typography variant={'body1'}>News</Typography>
                    </Stack>
                    <Divider />
                </Stack>
                <Stack className={classes.tagStack}>
                    {tags.map((tag) => {
                        return (
                            <Chip
                                key={tag.key}
                                variant={tag.key === (isNewsCategoryAll ? 'All' : selectedSecurity?.companyCode || selectedSecurity?.code) ? 'filled' : 'outlined'}
                                label={tag.label}
                                onClick={() => handleTagClick(tag.key)}
                            />
                        )
                    })}
                </Stack>
                {loading ? (
                    <ListSkeleton variant="news" />
                ) : news.length < 1 ? (
                    <Stack className={classes.emptyState}>
                        <EmptyState preview={<GhostRows variant="news" />} text="No news for this selection yet." />
                    </Stack>
                ) : (
                    <Grid item className={classes.scrollableGrid} id="scrollableGrid">
                        <InfiniteScroll
                            dataLength={news.length}
                            next={fetchData}
                            hasMore={news.length < totalArticles}
                            loader={<ListSkeleton variant="news" rows={2} fill={false} />}
                            endMessage={
                                news.length > 0 ? (
                                    <Stack className={classes.endMessage}>
                                        <Typography variant="caption" color={'text.secondary'}>
                                            You are all caught up!
                                        </Typography>
                                    </Stack>
                                ) : null
                            }
                            scrollableTarget="scrollableGrid"
                        >
                            {news.map((newsArticle) => {
                                return <NewsWidget newsArticle={newsArticle} key={`${newsArticle.providerCode}-${newsArticle.title}`} />
                            })}
                        </InfiniteScroll>
                    </Grid>
                )}
            </Stack>
        </Paper>
    )
}

export default memo(MarketNews)
